import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { api } from '../../services/api'
import './CreateQuiz.css'
import { MainLayout } from '../../Layout/MainLayout'

interface Alternative {
    text: string
    correct: boolean
    order: number
}

interface Question {
    statement: string
    explanation: string
    order: number
    weight: string
    alternatives: Alternative[]
}

function emptyQuestion(order: number): Question {
    return {
        statement: '',
        explanation: '',
        order,
        weight: '1.0',
        alternatives: [
            { text: '', correct: false, order: 0 },
            { text: '', correct: false, order: 1 },
            { text: '', correct: false, order: 2 },
            { text: '', correct: false, order: 3 },
        ]
    }
}

export function CreateQuiz() {
    const navigate = useNavigate()
    const { state } = useLocation()
    const courseId = state?.courseId

    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)
    const [error, setError] = useState('')

    const [form, setForm] = useState({
        title: '',
        instructions: '',
        minimuGrade: '7.0',
        maxAttempts: '2',
        timeLimitMin: '',
        shuffleQuestions: true,
        shuffleAlternatives: true,
        requireCompletedVideo: true,
    })

    const [questions, setQuestions] = useState<Question[]>([emptyQuestion(0)])

    function setField(field: string, value: any) {
        setForm(prev => ({ ...prev, [field]: value }))
        setError('')
    }

    // Questão
    function setQuestion(qi: number, field: keyof Question, value: any) {
        setQuestions(prev => prev.map((q, i) => i === qi ? { ...q, [field]: value } : q))
    }

    function addQuestion() {
        setQuestions(prev => [...prev, emptyQuestion(prev.length)])
    }

    function removeQuestion(qi: number) {
        setQuestions(prev => prev.filter((_, i) => i !== qi).map((q, i) => ({ ...q, order: i })))
    }

    // Alternativa
    function setAlternative(qi: number, ai: number, field: keyof Alternative, value: any) {
        setQuestions(prev => prev.map((q, i) => {
            if (i !== qi) return q
            return {
                ...q,
                alternatives: q.alternatives.map((a, j) => {
                    if (j !== ai) return a
                    return { ...a, [field]: value }
                })
            }
        }))
    }

    function setCorrect(qi: number, ai: number) {
        setQuestions(prev => prev.map((q, i) => {
            if (i !== qi) return q
            return {
                ...q,
                alternatives: q.alternatives.map((a, j) => ({ ...a, correct: j === ai }))
            }
        }))
    }

    function addAlternative(qi: number) {
        setQuestions(prev => prev.map((q, i) => {
            if (i !== qi) return q
            return {
                ...q,
                alternatives: [...q.alternatives, { text: '', correct: false, order: q.alternatives.length }]
            }
        }))
    }

    function removeAlternative(qi: number, ai: number) {
        setQuestions(prev => prev.map((q, i) => {
            if (i !== qi) return q
            return {
                ...q,
                alternatives: q.alternatives
                    .filter((_, j) => j !== ai)
                    .map((a, j) => ({ ...a, order: j }))
            }
        }))
    }

    function validate() {
        if (!courseId)              return 'Curso não identificado'
        if (!form.title.trim())     return 'Título é obrigatório'
        if (questions.length === 0) return 'Adicione ao menos uma questão'

        for (const [i, q] of questions.entries()) {
            if (!q.statement.trim())        return `Questão ${i + 1} sem enunciado`
            if (q.alternatives.length < 2)  return `Questão ${i + 1} precisa de ao menos 2 alternativas`
            const hasCorrect = q.alternatives.some(a => a.correct)
            if (!hasCorrect)                return `Questão ${i + 1} não tem alternativa correta marcada`
            for (const [j, a] of q.alternatives.entries()) {
                if (!a.text.trim()) return `Questão ${i + 1} — alternativa ${j + 1} está vazia`
            }
        }
        return null
    }

    async function handleSubmit() {
        const err = validate()
        if (err) { setError(err); return }

        setLoading(true)
        setError('')
        try {
            await api.post(`/course/${courseId}/quizzes`, {
                courseId,
                title:                 form.title,
                instructions:          form.instructions || undefined,
                minimuGrade:           Number(form.minimuGrade),
                maxAttempts:           Number(form.maxAttempts),
                timeLimitMin:          form.timeLimitMin ? Number(form.timeLimitMin) : undefined,
                shuffleQuestions:      form.shuffleQuestions,
                shuffleAlternatives:   form.shuffleAlternatives,
                requireCompletedVideo: form.requireCompletedVideo,
                questions: questions.map(q => ({
                    statement:    q.statement,
                    explanation:  q.explanation || undefined,
                    order:        q.order,
                    weight:       Number(q.weight),
                    alternatives: q.alternatives,
                }))
            })
            setSuccess(true)
            setTimeout(() => navigate('/admin'), 1800)
        } catch (e: any) {
            setError(e.response?.data?.message || 'Erro ao criar Quiz')
        } finally {
            setLoading(false)
        }
    }

    const letters = ['A', 'B', 'C', 'D', 'E']

    if (success) return (
        <MainLayout>
            <div className="cu-success-wrap">
                <div className="cu-success-icon">✓</div>
                <h2 className="serif">Quiz criado!</h2>
                <p>Redirecionando para o painel...</p>
            </div>
        </MainLayout>
    )

    return (
        <MainLayout>
            <div className="container fade-in">
                <div className="breadcrumb">
                    <a onClick={() => navigate('/admin')}>Admin</a>
                    <span className="sep">›</span>
                    <span>Novo Quiz</span>
                </div>

                <div className="cu-layout">
                    <div className="cu-header">
                        <div className="kicker">Gestão de Quiz</div>
                        <h1 className="serif">Novo Quiz</h1>
                        <p>Preencha os dados e adicione as questões.</p>
                    </div>

                    <div className="cu-form-card">
                        {error && <div className="cu-error">{error}</div>}

                        <div className="cu-section-label">Dados do Quiz</div>
                        <div className="cu-row">
                            <div className="field" style={{ flex: 1 }}>
                                <label>Título *</label>
                                <input
                                    type="text"
                                    value={form.title}
                                    onChange={e => setField('title', e.target.value)}
                                    placeholder="Título do Quiz"
                                    autoFocus
                                />
                            </div>
                            <div className="field" style={{ flex: 2 }}>
                                <label>Instruções</label>
                                <input
                                    type="text"
                                    value={form.instructions}
                                    onChange={e => setField('instructions', e.target.value)}
                                    placeholder="Instruções para o aluno"
                                />
                            </div>
                        </div>

                        <div className="cu-row">
                            <div className="field" style={{ flex: 1 }}>
                                <label>Nota mínima</label>
                                <input
                                    type="number"
                                    value={form.minimuGrade}
                                    onChange={e => setField('minimuGrade', e.target.value)}
                                    placeholder="7.0"
                                    min="0" max="10" step="0.5"
                                />
                            </div>
                            <div className="field" style={{ flex: 1 }}>
                                <label>Máx. tentativas</label>
                                <input
                                    type="number"
                                    value={form.maxAttempts}
                                    onChange={e => setField('maxAttempts', e.target.value)}
                                    placeholder="2"
                                    min="1"
                                />
                            </div>
                            <div className="field" style={{ flex: 1 }}>
                                <label>Tempo limite (min)</label>
                                <input
                                    type="number"
                                    value={form.timeLimitMin}
                                    onChange={e => setField('timeLimitMin', e.target.value)}
                                    placeholder="Sem limite"
                                    min="1"
                                />
                            </div>
                        </div>

                        <div className="cu-row" style={{ gap: 24, marginBottom: 8 }}>
                            {[
                                { field: 'shuffleQuestions',      label: 'Embaralhar questões' },
                                { field: 'shuffleAlternatives',   label: 'Embaralhar alternativas' },
                                { field: 'requireCompletedVideo', label: 'Exigir vídeos concluídos' },
                            ].map(opt => (
                                <label key={opt.field} className="cq-toggle">
                                    <input
                                        type="checkbox"
                                        checked={(form as any)[opt.field]}
                                        onChange={e => setField(opt.field, e.target.checked)}
                                    />
                                    <span className="cq-toggle-track" />
                                    {opt.label}
                                </label>
                            ))}
                        </div>

                        <div className="cu-divider" />

                        {/* Questões */}
                        <div className="cq-questions-header">
                            <div className="cu-section-label" style={{ margin: 0 }}>
                                Questões ({questions.length})
                            </div>
                            <button className="btn btn-ghost" style={{ fontSize: 13, padding: '6px 14px' }} onClick={addQuestion}>
                                + Adicionar questão
                            </button>
                        </div>

                        {questions.map((q, qi) => (
                            <div key={qi} className="cq-question-card">
                                <div className="cq-question-head">
                                    <span className="cq-q-num">Questão {qi + 1}</span>
                                    {questions.length > 1 && (
                                        <button className="cq-remove" onClick={() => removeQuestion(qi)}>✕ Remover</button>
                                    )}
                                </div>

                                <div className="cu-row">
                                    <div className="field" style={{ flex: 3 }}>
                                        <label>Enunciado *</label>
                                        <input
                                            type="text"
                                            value={q.statement}
                                            onChange={e => setQuestion(qi, 'statement', e.target.value)}
                                            placeholder="Qual é a pergunta?"
                                        />
                                    </div>
                                    <div className="field" style={{ flex: 1 }}>
                                        <label>Peso</label>
                                        <input
                                            type="number"
                                            value={q.weight}
                                            onChange={e => setQuestion(qi, 'weight', e.target.value)}
                                            placeholder="1.0"
                                            min="0.1" step="0.1"
                                        />
                                    </div>
                                </div>

                                <div className="field">
                                    <label>Explicação (mostrada após responder)</label>
                                    <input
                                        type="text"
                                        value={q.explanation}
                                        onChange={e => setQuestion(qi, 'explanation', e.target.value)}
                                        placeholder="Explique a resposta correta..."
                                    />
                                </div>

                                <div className="cq-alternatives-label">
                                    <span>Alternativas — clique no círculo para marcar a correta</span>
                                    {q.alternatives.length < 5 && (
                                        <button className="cq-add-alt" onClick={() => addAlternative(qi)}>+ alternativa</button>
                                    )}
                                </div>

                                {q.alternatives.map((alt, ai) => (
                                    <div key={ai} className="cq-alternative">
                                        <button
                                            className={`cq-correct-btn ${alt.correct ? 'on' : ''}`}
                                            onClick={() => setCorrect(qi, ai)}
                                            title="Marcar como correta"
                                        >
                                            {letters[ai]}
                                        </button>
                                        <input
                                            type="text"
                                            value={alt.text}
                                            onChange={e => setAlternative(qi, ai, 'text', e.target.value)}
                                            placeholder={`Alternativa ${letters[ai]}`}
                                        />
                                        {q.alternatives.length > 2 && (
                                            <button className="cq-remove-alt" onClick={() => removeAlternative(qi, ai)}>✕</button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))}

                        <button
                            className="btn btn-ghost btn-full"
                            style={{ marginTop: 8 }}
                            onClick={addQuestion}
                        >
                            + Adicionar questão
                        </button>

                        <div className="cu-divider" />

                        <div className="cu-actions">
                            <button className="btn btn-ghost" onClick={() => navigate('/admin')} disabled={loading}>
                                Cancelar
                            </button>
                            <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                                {loading ? 'Criando...' : 'Criar Quiz →'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    )
}